import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { CampusExercise } from '@chancenwerk/campus-learning-content/entities/CampusExercise.js';
import { CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { ContentResultEventlog } from '@chancenwerk/db/eventlogs/ContentResultEventlog.js';

@Entity()
export class CampusExerciseAnswer extends AdornisEntity {
  public static override _class = 'CampusExerciseAnswer';

  @Field(type => [String], {
    default: v => v ?? [],
  })
  public mathAnswers!: string[];

  @Field(type => String)
  public textAnswer: Maybe<string>;
}

const resolveValidateCampusExercise = (
  userID: string,
  contentID: string,
  lessonID: string,
  answer: Maybe<CampusExerciseAnswer>,
) => {
  return async (gqlFields: void) => {
    if (!answer) throw new Error('No Answer Object given!');

    const lessonCol = await getCollection(CampusLesson._class);
    const lesson = await lessonCol.findOne<CampusLesson>({ _id: lessonID });
    if (!lesson) throw new Error('Invalid lesson');
    const contentCol = await getCollection(CampusExercise._class);
    const content = await contentCol.findOne<CampusExercise>({ _id: contentID });
    if (!content) throw new Error('Invalid content id');

    let result = true;
    if (content.questionAnswerVariableFields) {
      content.questionAnswerVariableFields.match(CampusExercise.latexExpressionRegex)?.forEach((elem, index) => {
        if (`$${answer.mathAnswers[index]}$` !== elem) result = false;
      });
    }

    await new ContentResultEventlog({
      userID: userID,
      answer: answer.mathAnswers.join(',') + ' | ' + answer.textAnswer,
      lessonID: lessonID,
      contentID: contentID,
      result,
    }).create();

    return result;
  };
};

export const validateCampusExercise = registerMutation({
  type: () => Boolean,
  operationName: 'validateCampusExercise',
  params: [
    { type: () => String, name: 'userId' },
    { type: () => String, name: 'contentId' },
    { type: () => String, name: 'lessonId' },
    { type: () => CampusExerciseAnswer, name: 'answer' },
  ],
  resolve: resolveValidateCampusExercise,
});
